import { postInquiryMessage } from './postInquiryMessage';
import { PrimumChatTransport, type PrimumConversationDetails } from './primumTransport';

import { onInboxSignal } from '@/services/conversations/inboxSignals';
import { getInquiryMessages } from '@/services/getInquiryMessages';
import { getInquiryV2QueryOptions, type GetInquiryV2Response } from '@/services/getInquiryV2';
import { selectIsInboxLive, useInboxRealtimeStore } from '@/services/stores/inboxRealtimeStore';
import { queryClient } from '@/util/queryClient';

/**
 * Who takes part in an inquiry's conversation: its recorded participants plus
 * its author and recipient. The two always agree for an inquiry created today;
 * the union covers older rows whose participant list was never filled in, so a
 * real participant is never refused.
 */
export function inquiryParticipantIds(inquiry: GetInquiryV2Response): number[] {
  const ids = new Set<number>(inquiry?.participantIds ?? []);

  for (const participant of [inquiry?.author, inquiry?.recipient]) {
    if (typeof participant?.id === 'number') {
      ids.add(participant.id);
    }
  }

  return [...ids];
}

/**
 * What a Twilio conversation carries on itself, read from the inquiry instead.
 * The title is what Twilio stores as the conversation's attributes, and the
 * header shows it as the subject. A conversation still starting up reads as
 * inactive, as the webhook maps it.
 */
export function inquiryConversationDetails(
  inquiry: GetInquiryV2Response
): PrimumConversationDetails {
  const status = inquiry?.inquiryStatus;

  return {
    participantIds: inquiryParticipantIds(inquiry),
    attributes: inquiry?.title ?? {},
    status: status === 'active' || status === 'closed' ? status : 'inactive',
  };
}

/**
 * The Primum transport wired to the app: messages and the inquiry from our API,
 * hints from the app-wide inbox socket. The inquiry is read through the same
 * query the chat reads it with, so opening a conversation does not fetch it
 * twice.
 */
export function createPrimumChatTransport(viewerId: string): PrimumChatTransport {
  return new PrimumChatTransport({
    viewerId,
    fetchMessages: getInquiryMessages,
    postMessage: postInquiryMessage,
    fetchConversationDetails: async (conversationId) =>
      inquiryConversationDetails(
        await queryClient.fetchQuery(getInquiryV2QueryOptions({ conversationId }))
      ),
    subscribeToSignals: onInboxSignal,
    isSignalLive: () => selectIsInboxLive(useInboxRealtimeStore.getState()),
  });
}
